import { useUser } from "@/contexts/UserContext";
import { useAxiosInstance } from "@/utils/useAxiosInstance";
import { AreaChart } from "@tinybirdco/charts";
import { format, formatInTimeZone } from "date-fns-tz";
import { useEffect, useState } from "react";
import BranchRequiredWrapper from "../banners/BranchRequiredWrapper";
import PageLoader from "../loader/PageLoader";

const InfoCard = ({
  title,
  info,
}: {
  title: string;
  info: number | string;
}) => {
  return (
    <div key={title} className="rounded-lg bg-white p-6">
      <h3 className="mb-2 text-gray-400">{title}</h3>
      <p className="mb-2 text-3xl font-bold">{info}</p>
    </div>
  );
};

const colorPalette = [
  "#FF6B6C",
  "#27F795",
  "#008060",
  "#0EB1B9",
  "#9263AF",
  "#5A6FC0",
  "#86BFDB",
  "#FFC145",
  "#DC82C8",
  "#FFC0F1",
];

const GetDates = (period: string): { start: Date; end: Date } => {
  const branchData = localStorage.getItem('selectedBranch');
  const branchTimezone = branchData ? JSON.parse(branchData).timezone : 'UTC';

  const now = new Date();
  
  const calculateDate = (offset: number, unit: "days" | "years" = "days"): Date => {
    const date = new Date(now);
    
    if (unit === "years") {
      date.setFullYear(date.getFullYear() - offset);
    } else {
      date.setDate(date.getDate() - offset);
    }
    
    return date;
  };

  const periodMapping: Record<string, { start: Date; end: Date }> = {
    "All Data": { 
      start: new Date("2020-01-01T00:00:00Z"), 
      end: now 
    },
    "12 Months": { start: calculateDate(1, "years"), end: now },
    "30 Days": { start: calculateDate(30), end: now },
    "7 Days": { start: calculateDate(7), end: now },
    "24 Hour": { start: calculateDate(1), end: now },
  };

  return periodMapping[period] || periodMapping["24 Hour"];
};

const Analytics = () => {
  const { user } = useUser();
  const defaultPeriod = "24 Hour";
  const { start: initialStartDate, end: initialEndDate } =
    GetDates(defaultPeriod);

  const [loading, setLoading] = useState(false);
  const [datePicker, setDatePicker] = useState(defaultPeriod);
  const [startDate, setStartDate] = useState<Date | null>(initialStartDate);
  const [endDate, setEndDate] = useState<Date | null>(initialEndDate);
  const [axiosInstance] = useAxiosInstance();
  const [form, setForm] = useState({
    visits: 0,
    unique_visits: 0,
    sales: 0,
    orders: 0,
    visit_trend: {} as any,
    sales_trend: {} as any,
    top_products: [] as any,
    device_types: {} as any,
    customer_order_patterns: [] as any,
  });

  const handleDateRange = (period: string) => {
    setDatePicker(period);
    const { start, end } = GetDates(period);

    const utcStartTime = start.getTime();
    const utcEndTime = end.getTime();

    const utcStart = new Date(utcStartTime);
    const utcEnd = new Date(utcEndTime);

    setStartDate(utcStart);
    setEndDate(utcEnd);
  };

  const formatDate = (date: Date) => {
    return formatInTimeZone(date, 'UTC', "yyyy-MM-dd HH:mm:ss");
  };
  useEffect(() => {
    (async () => {
      if (!axiosInstance) return;
      setLoading(true);
      const start = startDate ? formatDate(startDate) : "";
      const end = endDate ? formatDate(endDate) : "";
      
      console.log('API call parameters:', {
        start,
        end,
        startDate: startDate?.toString(),
        endDate: endDate?.toString()
      });
      
      try {
        const [
          branchVisitRes,
          orderRes,
          customerOrderRes,
          topProductRes,
          deviceRes,
        ] = await Promise.all([
          axiosInstance.get(
            `/tinybird/get-events?pipe_name=branch_visitors&start_date=${start}&end_date=${end}&branch_id=${user?.selectedBranch?.id}`,
          ),
          axiosInstance.get(
            `/tinybird/get-events?pipe_name=order_analytics&start_date=${start}&end_date=${end}&branch_id=${user?.selectedBranch?.id}`,
          ),
          axiosInstance.get(
            `/tinybird/get-events?pipe_name=customer_order_patterns&start_date=${start}&end_date=${end}&branch_id=${user?.selectedBranch?.id}`,
          ),
          axiosInstance.get(
            `/tinybird/get-events?pipe_name=top_products_analysis&start_date=${start}&end_date=${end}&branch_id=${user?.selectedBranch?.id}`,
          ),
          axiosInstance.get(
            `/tinybird/get-events?pipe_name=top_devices_analysis&start_date=${start}&end_date=${end}&branch_id=${user?.selectedBranch?.id}`,
          ),
        ]);
        setForm((prev) => ({
          ...prev,
          sales: orderRes.data.data?.reduce(
            (acc: number, item: any) => acc + item.total_revenue,
            0,
          ),
          orders: orderRes.data.data?.reduce(
            (acc: number, item: any) => acc + item.total_orders,
            0,
          ),
          device_types: deviceRes.data.data,
          visits: branchVisitRes.data.data?.reduce(
            (acc: number, item: any) => acc + item.total_visits,
            0,
          ),
          unique_visits: branchVisitRes.data.data?.reduce(
            (acc: number, item: any) => acc + item.unique_visitors,
            0,
          ),
          visit_trend: branchVisitRes.data,
          sales_trend: orderRes.data,
          top_products: topProductRes.data.data,
          customer_order_patterns: customerOrderRes.data.data,
        }));
      } catch (error) {
        console.error("Error fetching sales data:", error);
      } finally {
        setLoading(false);
      }
    })();
  }, [startDate, endDate, axiosInstance, user?.selectedBranch?.id]);

  return (
    <PageLoader isFetching={loading}>
      <BranchRequiredWrapper>
        <div className="min-h-screen bg-[#F9F9FC]">
          <header className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-medium text-[#1D1F2C]">
                Welcome Back {user?.first_name} {user?.last_name}
              </h1>
              <p className="text-[#667085]">
                Here is your business analysis report
              </p>
            </div>
          </header>

          <div className="grid grid-cols-1 xl:grid-cols-2">
            <div className="hidden xl:block" />
            <div className="mb-8 flex justify-center space-x-1 md:space-x-2 lg:justify-end">
              <div className="rounded-md bg-white">
                {["All Data", "12 Months", "30 Days", "7 Days", "24 Hour"].map(
                  (period) => (
                    <button
                      key={period}
                      onClick={() => handleDateRange(period)}
                      className={`m-1 rounded-md px-2 py-1 text-[12px] text-red-500 md:px-4 md:text-base ${
                        datePicker === period
                          ? "bg-red-500 text-white"
                          : "hover:bg-red-500 hover:text-white active:bg-red-500 active:text-white"
                      }`}
                    >
                      {period}
                    </button>
                  ),
                )}
              </div>
            </div>
          </div>
          <div className="mb-4 grid grid-cols-2 gap-6 lg:grid-cols-4">
            {[
              {
                title: "Total Visitors",
                value: form.visits,
              },
              {
                title: "Unique Visitors",
                value: form.unique_visits,
              },
              { title: "Total Order", value: form.orders },
              {
                title: "Total Transaction",
                value: `$${form.sales.toFixed(2)}`,
              },
            ].map((metric) => (
              <InfoCard title={metric.title} info={metric.value} />
            ))}
          </div>
          <div className="mb-4 grid grid-cols-1 lg:grid-cols-3 lg:space-x-4 lg:space-y-0">
            <div className="col-span-2 hidden rounded-lg bg-white p-6 md:block">
              <h3 className="text-xl font-bold">Visitors</h3>
              <span className="text-gray-400">
                This chart shows the visits count across a certain time period
              </span>
              {form.visit_trend.data?.length > 0 ? (
                <AreaChart
                  index="date"
                  data={form.visit_trend.data}
                  meta={form.visit_trend.meta}
                  colorPalette={colorPalette}
                  categories={["total_visits"]}
                  height="300px"
                  stacked={true}
                />
              ) : (
                <div className="flex h-80 items-center justify-center">
                  No data available
                </div>
              )}
            </div>
            <div className="rounded-lg bg-white p-2">
              <h3 className="mb-4 text-xl font-bold">Device Category</h3>
              {form.device_types?.length > 0 ? (
                <table className="w-full" cellPadding="7px">
                  <thead>
                    <tr className="border-y border-[#BBBBBB] text-left">
                      <th>Device</th>
                      <th>Visits</th>
                      <th>Percentage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.device_types?.map((item: any, key: number) => (
                      <tr key={key} className="border-y border-[#BBBBBB]">
                        <td>{item.device_type}</td>
                        <td>{item.total_visits}</td>
                        <td>
                          {((item.total_visits / form.visits) * 100).toFixed(2)}
                          %
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="flex h-80 items-center justify-center">
                  No data available
                </div>
              )}
            </div>
          </div>
          <div className="mb-4 grid grid-cols-1 lg:grid-cols-3 lg:space-x-4 lg:space-y-0">
            <div className="col-span-2 hidden rounded-lg bg-white p-6 md:block">
              <h3 className="text-xl font-bold">Sales</h3>
              <span className="text-gray-400">
                This chart depicts the variation of orders and revenue over a
                period of time
              </span>
              {form.sales_trend.data?.length > 0 ? (
                <AreaChart
                  index="date"
                  data={form.sales_trend.data}
                  meta={form.sales_trend.meta}
                  colorPalette={colorPalette}
                  categories={["total_orders"]}
                  height="300px"
                />
              ) : (
                <div className="flex h-80 items-center justify-center">
                  No data available
                </div>
              )}
            </div>
            <div className="max-w-full overflow-x-auto rounded-lg bg-white p-4">
              <h3 className="mb-4 text-xl font-bold">Top Products</h3>
              {form.top_products?.length > 0 ? (
                <table className="w-full" cellPadding="7">
                  <thead>
                    <tr className="border-y border-[#BBBBBB] text-left">
                      <th className="px-4 py-2">Product</th>
                      <th className="px-4 py-2">Count</th>
                      <th className="px-4 py-2">Sales</th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.top_products?.map((item: any, key: number) => (
                      <tr key={key} className="border-y border-[#BBBBBB]">
                        <td className="flex items-center px-4 py-2">
                          <img
                            src={item.image}
                            alt={item.product_name}
                            className="mr-2 h-7 w-7 rounded-md bg-gray-600"
                          />
                          <span>{item.product_name}</span>
                        </td>
                        <td className="px-4 py-2 text-center">
                          <span>{item.total_quantity}</span>
                        </td>
                        <td className="px-4 py-2 text-left">
                          ${Number(item.total_revenue ?? "0.00").toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="flex h-80 items-center justify-center">
                  No data available
                </div>
              )}
            </div>
          </div>
          <div className="max-w-full overflow-x-auto rounded-lg bg-white p-4">
            <h3 className="mb-4 text-xl font-bold">Top Customers</h3>
            {form.customer_order_patterns?.length > 0 ? (
              <table className="w-full" cellPadding="4">
                <thead>
                  <tr className="border-y border-[#BBBBBB] text-left">
                    <th className="py-2 pr-4">Name</th>
                    <th className="py-2 pr-4">Last Order</th>
                    <th className="py-2 pr-4">Smallest Order</th>
                    <th className="py-2 pr-4">Largest Order</th>
                    <th className="py-2 pr-4">Average Order Value</th>
                    <th className="py-2 pr-4">Total Order</th>
                    <th className="py-2 pr-4">Total Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {form.customer_order_patterns?.map(
                    (item: any, key: number) => (
                      <tr key={key} className="border-y border-[#BBBBBB]">
                        <td className="py-2">
                          <span>{item.customer_name}</span>
                        </td>
                        <td className="py-2">
                          <span>{item.last_order_date}</span>
                        </td>
                        <td className="py-2">
                          <span>
                            ${Number(item.smallest_order ?? "0.00").toFixed(2)}
                          </span>
                        </td>
                        <td className="py-2">
                          <span>
                            ${Number(item.largest_order ?? "0.00").toFixed(2)}
                          </span>
                        </td>
                        <td className="py-2">
                          <span>
                            ${Number(item.avg_order_value ?? "0.00").toFixed(2)}
                          </span>
                        </td>
                        <td className="py-2">
                          <span>{item.order_count}</span>
                        </td>
                        <td className="py-2">
                          ${Number(item.total_spent ?? "0.00").toFixed(2)}
                        </td>
                      </tr>
                    ),
                  )}
                </tbody>
              </table>
            ) : (
              <div className="flex h-80 items-center justify-center">
                No data available
              </div>
            )}
          </div>
        </div>
      </BranchRequiredWrapper>
    </PageLoader>
  );
};

export default Analytics;
